/**
 * Types and constants for PO Lines Tab components
 */
import { PurchaseOrderLine } from '../../../../lib/api';
import { CUSTOMER_REQUIRED_DATE, MRP_SCHEDULED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE } from '../../../../config/dateLabels';

// CR-3406: Column configuration for visibility management
// CR-3846: Added order field for column reordering
export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  minWidth: string;
  order: number;  // CR-3846: Column display order
}

// CR-3850: Added additional columns from old platform
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  // EXPEDITING-PARITY PR 2, Aaron 17 Sep 2026. Two changes, one ruling.
  // "Ack Status" -> "Status": *"I am not a fan of Ack Status, it looks
  // unprofessional and it should just be Status."*
  // And the separate On Hold column RETIRES into it, because a line has one
  // status and a held line's status is the hold: *"the status IS On Hold
  // (Customer/Supplier), it cannot be both on hold and in motion."* Hold Reason
  // stays — that is the WHY, which a status does not carry. A saved column
  // config naming 'on_hold' simply stops matching a column and is dropped by
  // the id merge, so nobody has to reset their picker.
  { id: 'acknowledge_status', label: 'Status', visible: true, sortable: true, minWidth: '170px', order: 0 },
  { id: 'action', label: 'Action', visible: true, sortable: false, minWidth: '100px', order: 1 },
  { id: 'is_critical', label: 'Critical', visible: true, sortable: true, minWidth: '80px', order: 2 },
  { id: 'hold_reason', label: 'Hold Reason', visible: true, sortable: false, minWidth: '120px', order: 4 },
  { id: 'next_action', label: 'Next Action', visible: true, sortable: true, minWidth: '120px', order: 5 },
  // CR-6510: procurement traceability — whether a deviation has been raised
  // against the line and where it stands. Off by default: it is empty for
  // almost every line, and customers without concessions enabled would get a
  // permanently blank column.
  { id: 'concession_status', label: 'Concession', visible: false, sortable: false, minWidth: '150px', order: 5.5 },
  { id: 'po_number', label: 'PO Number', visible: true, sortable: true, minWidth: '140px', order: 6 },
  { id: 'line_number', label: 'Line #', visible: true, sortable: true, minWidth: '60px', order: 7 },
  { id: 'erp_order_type', label: 'ERP Order Type', visible: true, sortable: false, minWidth: '120px', order: 7.5 },
  { id: 'customer_name', label: 'Customer', visible: true, sortable: true, minWidth: '150px', order: 8 },
  { id: 'supplier_name', label: 'Supplier', visible: true, sortable: true, minWidth: '220px', order: 9 },
  { id: 'supplier_group_name', label: 'Supplier Group', visible: false, sortable: true, minWidth: '150px', order: 10 },  // CR-3850
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, minWidth: '160px', order: 12 },
  // CR-7308 rev 2 (Aaron, 18 Sep 2026): BOTH, and named so the difference is
  // readable without opening anything. "Part Description" is the part master's
  // name for the part; "Order Description" is the text typed on THIS order line
  // — drill instructions, release requirements, material specs. Legacy's grid
  // carries only the second and calls it "Description", which is why the same
  // word meant two things. Sarah asked for both.
  { id: 'part_description', label: 'Part Description', visible: true, sortable: false, minWidth: '200px', order: 12.5 },
  { id: 'description', label: 'Order Description', visible: true, sortable: true, minWidth: '200px', order: 13 },
  { id: 'quantity', label: 'Qty Ordered', visible: true, sortable: true, minWidth: '140px', order: 14 },
  // CR-7468 (Aaron, 22 Sep): the list carries the same five figures as the line
  // screen, in the same order and under the same words -- ordered, delivered,
  // rejected, accepted, outstanding -- so a buyer scanning the book sees what a
  // buyer opening the line sees.
  //
  // Neither of the two new ones is sortable: both are derived per page from the
  // receipts and the NCRs, like the two warehouse columns below. The ACCEPTED
  // column keeps the id `received_qty` so saved layouts survive; its value has
  // been the accepted quantity since CR-7417 and only its label moved.
  { id: 'delivered_qty', label: 'Qty Delivered', visible: true, sortable: false, minWidth: '150px', order: 14.5 },
  { id: 'received_qty', label: 'Qty Accepted', visible: true, sortable: true, minWidth: '160px', order: 15 },
  { id: 'rejected_qty', label: 'Qty Rejected', visible: true, sortable: false, minWidth: '150px', order: 14.7 },
  // CR-7743: this line's share of the supplier's own warehouse stock, shared
  // oldest open line first (call_off/services/po_line_supply.py). Distinct from
  // received_qty, which is what the customer has GRN'd. Not sortable: worked
  // out per page, not a model field.
  { id: 'delivered_warehouse_qty', label: 'Delivered into Warehouse', visible: true, sortable: false, minWidth: '200px', order: 16 },
  // CR-7163 (a): supplier-side Fulfilment %, driven by supplier warehouse
  // stock — delivered_warehouse_qty / outstanding_qty. Sits immediately after
  // the qty it derives from so the pair reads together. Fractional order
  // deliberately: 16-21 are taken, and renumbering them would interleave
  // oddly for users whose saved column order is merged by id from
  // localStorage. Not sortable — derived client-side, not a model field.
  { id: 'warehouse_fulfilment_pct', label: 'Fulfilment %', visible: true, sortable: false, minWidth: '150px', order: 16.5 },
  // CR-7743: what the supplier still has to make — outstanding less what is in
  // transit and in their warehouse. Worked out per page, so not sortable.
  { id: 'remaining_to_produce_qty', label: 'Remaining to Produce', visible: true, sortable: false, minWidth: '190px', order: 16.6 },
  // CR-7743: days from today to the Latest Supplier Promise Date while there is
  // still something to make; red at 15 days or less. Sorts by that date.
  { id: 'days_left_to_produce', label: 'Days Left to Produce', visible: true, sortable: true, minWidth: '190px', order: 16.7 },

  // CR-7468 (Aaron, 22 Sep): spelled out, like every other quantity on the
  // grid — the exports have said `Qty Outstanding` all along. MOVED to sit with
  // the other four rather than two columns further right: the five are one
  // sentence and they read as one only when they are adjacent. A saved layout
  // merges by id and keeps its own order, so this changes the default only.
  { id: 'outstanding_qty', label: 'Qty Outstanding', visible: true, sortable: true, minWidth: '140px', order: 15.7 },
  { id: 'pct_delivered', label: '% Delivered', visible: true, sortable: true, minWidth: '130px', order: 18 },
  { id: 'unit_price', label: 'Unit Price', visible: true, sortable: true, minWidth: '140px', order: 19 },
  { id: 'total_price', label: 'Total', visible: true, sortable: true, minWidth: '110px', order: 20 },
  // Batch 4 (Aaron, 17 Sep 2026): *"we need to add the missing currency column
  // that is in the legacy platform."* It existed, hidden — and hiding it is
  // what made the currency glyphs on every money cell look load-bearing.
  { id: 'currency', label: 'Currency', visible: true, sortable: true, minWidth: '100px', order: 21 },
  // CR-4694 round 2 rework: the Customer Required Date (delivery_date) column
  // removed from the table view per spec — the field was being populated by
  // the import path that should have been writing to MRP/ERP Scheduled, so
  // its values were misleading. The delivery_date model field is unchanged
  // (OTIF, on-time, and many other downstream calculations still depend on
  // it), only the surface in the column picker is removed.
  // Batch 4: the three negotiated dates read ODD, CRD, LSPD — the order Aaron
  // gave, which is also the order they happen in: the anchor, what the
  // customer now needs, what the supplier now promises. The promise date used
  // to sit back at order 22, before Buyer ID and away from the other two.
  { id: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE, visible: true, sortable: true, minWidth: '220px', order: 43.7 },
  { id: 'buyer_id', label: 'Buyer ID', visible: true, sortable: true, minWidth: '100px', order: 23 },
  { id: 'batch_serial_no', label: 'Batch/Serial No', visible: false, sortable: true, minWidth: '140px', order: 24 },
  { id: 'ppv', label: 'PPV', visible: false, sortable: true, minWidth: '100px', order: 25 },
  // CR-7416 (Aaron, 19 Sep): the measured OTIF VERDICT on the line, with the
  // same Pending / PASSED / FAILED badges the drill-downs print. Visible by
  // default — he asked for it on the grid, not hidden behind the picker.
  // Sits immediately before OTIF Review so the verdict and the decision
  // about it read as a pair. Fractional order for the same reason
  // `warehouse_fulfilment_pct` has one: renumbering 26+ would interleave
  // oddly for anyone whose saved column order is merged by id.
  // NOT sortable: the verdict is a function of two columns (`is_otif` and
  // `status`), and DRF orders by one field. A `Case` over two indexed local
  // columns would in fact be cheap — the 23.9s measurement this comment used
  // to cite belongs to `ncr_rejection.accepted_qty_expr`, which is a different
  // expression over a different join, and borrowing it here was a claim about
  // a cost nobody had measured. If the sort is ever wanted, measure it.
  { id: 'otif_verdict', label: 'OTIF Status', visible: true, sortable: false, minWidth: '140px', order: 25.5 },
  // CR-7418 (Aaron, 19 Sep): ONE column, called OTIF Review everywhere in the
  // portal. Hidden by default and forced visible while the review pill is
  // active (POLinesTable), because it answers a question nobody is asking on
  // the other eight tabs.
  //
  // The 'otif_overridden' yes/no column that stood at order 26 is folded into
  // it and its config row is gone — a grid carrying both would have answered
  // the same question twice, once with a status and once with a boolean that
  // could not tell "acknowledged" from "nobody has looked". (Deleted, not
  // DORMANT-marked: a column config row is data, and a retired row left in
  // this list would render.)
  //
  // NOT sortable, and the reason is not "computed": both underlying flags are
  // real indexed model columns and the old yes/no column DID sort on one. The
  // cell is a three-state derived in the browser from two of them, so a single
  // ORDER BY cannot sequence it — which is why round 4 also dropped its
  // SERVER_SORTABLE entry rather than leaving a header that silently did
  // nothing.
  { id: 'otif_review', label: 'OTIF Review', visible: false, sortable: false, minWidth: '140px', order: 25.9 },
  { id: 'packing_note_number', label: 'PnR Note No', visible: false, sortable: true, minWidth: '120px', order: 27 },
  { id: 'order_date', label: 'Order Date', visible: false, sortable: true, minWidth: '120px', order: 28 },
  { id: 'last_received_date', label: 'Last Received', visible: false, sortable: true, minWidth: '120px', order: 29 },
  { id: 'updated_at', label: 'Update Time', visible: false, sortable: true, minWidth: '150px', order: 30 },
  { id: 'required_date_change_count', label: 'Req Date Changes', visible: false, sortable: true, minWidth: '130px', order: 31 },
  { id: 'promised_date_change_count', label: 'Promise Date Changes', visible: false, sortable: true, minWidth: '150px', order: 32 },
  { id: 'pdf_attached_count', label: 'PDF Attached', visible: false, sortable: true, minWidth: '100px', order: 33 },
  { id: 'material_cert_count', label: 'Mat Certs', visible: false, sortable: true, minWidth: '100px', order: 34 },
  { id: 'contract_unit_price', label: 'Contract Unit Price', visible: false, sortable: true, minWidth: '140px', order: 35 },
  { id: 'contract_lot_price', label: 'Contract Lot Price', visible: false, sortable: true, minWidth: '140px', order: 36 },
  { id: 'contract_price', label: 'Contract Price', visible: false, sortable: true, minWidth: '130px', order: 37 },
  { id: 'drawing_number', label: 'Drawing No', visible: false, sortable: true, minWidth: '120px', order: 38 },
  { id: 'drawing_version', label: 'Drawing Issue', visible: false, sortable: true, minWidth: '100px', order: 39 },
  { id: 'outstanding_value', label: 'O/S Value', visible: false, sortable: true, minWidth: '120px', order: 40 },
  { id: 'converted_unit_price', label: 'Conv. Unit Price', visible: false, sortable: true, minWidth: '130px', order: 41 },
  { id: 'converted_total_value', label: 'Conv. Total', visible: false, sortable: true, minWidth: '120px', order: 42 },
  { id: 'converted_outstanding_value', label: 'Conv. O/S Value', visible: false, sortable: true, minWidth: '130px', order: 43 },
  // CR-5548 round 3: the ODD column previously bound to
  // PurchaseOrderLine.original_required_date (API key original_promise_date), an immutable snapshot of the
  // first agreed date kept "for tracking changes" (per the model field
  // help_text). Rounds 1 (commit d430d5dc) and 2 (commit ba5bb674) wrote
  // the post-accept date into delivery_date / schedule_date, so the
  // backend numbers were correct, but the UI column was reading the
  // wrong field — it kept showing the original promise even after a
  // change was accepted. Bind the column to delivery_date instead so it
  // reflects the currently-agreed delivery date the spec asks for.
  // The 'original_promise_date' field is still serialized for any caller
  // that genuinely needs the original snapshot (audit / scorecard
  // pre-accept lookups); we just stop using it as the user-facing
  // "Agreed Date" column source.
  // DATE-FIELD-CANON PR 2 (15 Sep 2026): the Customer Required Date
  // is back as a real column — the date expediting moves (API key
  // delivery_date; column customer_required_date) — visible by default.
  { id: 'delivery_date', label: CUSTOMER_REQUIRED_DATE, visible: true, sortable: true, minWidth: '160px', order: 43.6 },
  // Customer review 11 Sep: the ODD column shows the server's odd_date — the
  // ODD the verdict, OTIF and arrears measure against (original_required_date,
  // else the CRD, at PO-line grain; the slot date at schedule grain). An
  // accepted change moves the CRD but not that ODD, so the column follows the
  // metric, not the latest agreement. Its id is now 'odd_date' so what it
  // shows and what it sorts on agree; saved layouts under the old
  // id were reset by the storage-key bump below.
  // Batch 4: visible by default. It is the date every on-time verdict is
  // measured against, and it shipped hidden.
  { id: 'odd_date', label: ORIGINAL_REQUIRED_DATE, visible: true, sortable: true, minWidth: '140px', order: 43.5 },
  // CR-4694 round 2 rework: MRP/ERP Scheduled visible by default (the
  // import-driven date; kept alongside the Customer Required Date).
  { id: 'mrp_scheduled_date', label: MRP_SCHEDULED_DATE, visible: true, sortable: true, minWidth: '150px', order: 45 },
  { id: 'priority', label: 'Priority', visible: false, sortable: true, minWidth: '100px', order: 46 },
  { id: 'exchange_rate', label: 'Exchange Rate', visible: false, sortable: true, minWidth: '120px', order: 47 },  // CR-4679
];

// v2 (DATE-FIELD-CANON PR 2, 15 Sep 2026): the saved layout is merged BY ID,
// and the column that used to be saved as 'delivery_date' (the ODD) is now
// 'odd_date' while 'delivery_date' means the Customer Required Date. Reading a
// v1 layout would silently show users a different date under a column they
// had switched on, so v1 layouts are no longer read — everyone starts from
// the defaults once.
// v3 (EXPEDITING-PARITY PR 2, 17 Sep 2026): the Currency and ODD columns now
// ship visible and the three negotiated dates changed order. A saved layout is
// merged BY ID, so a v2 layout would keep both columns hidden and the old
// order — the two things this change exists to fix. Bumping the key resets it.
export const COLUMN_STORAGE_KEY = 'po_lines_column_config_v3';

// CR-4716: Commercial/internal fields restricted to customer users only.
// Supplier users must not see these in the table, column picker, or filters.
// Converted/exchange-rate fields are the customer's reporting-currency view —
// suppliers must only see the original transaction-currency price.
/**
 * CR-7163 (a): columns only the supplier sees. The mirror of
 * SUPPLIER_RESTRICTED_COLUMNS — those are hidden FROM suppliers, these are
 * hidden from everyone else.
 *
 * Fulfilment % answers "how much of what I still owe is sitting in my
 * warehouse ready to ship" — a supplier production question. The customer
 * already has "Delivered into Warehouse" (the qty) and "% Delivered" (their
 * own receipts), so this would only duplicate their view.
 *
 * CR-8613: Remaining to Produce and Days Left to Produce (CR-7743) were here
 * too; the customer wants to see them, so they are shown to both sides now
 * (CALL_OFF_SUPPLY_COLUMNS).
 */
export const SUPPLIER_ONLY_COLUMNS = [
  'warehouse_fulfilment_pct',
];

/**
 * Columns that only mean something on a call-off relationship — the
 * supplier's warehouse stock and what it still has to make — so they are
 * removed for a viewer with no call-off at all. Shown to both sides otherwise.
 */
export const CALL_OFF_SUPPLY_COLUMNS = [
  'delivered_warehouse_qty',
  'remaining_to_produce_qty',
  'days_left_to_produce',
];

// What is withheld from a supplier here is COMMERCIAL — prices, variance,
// rates, the buyer's identity — which is the rule CR-4716 wrote.
//
// Neither OTIF column is here. The verdict is what a supplier is scored on
// (Aaron, 20 Sep 2026), and the buyer's review of it is theirs to see, read
// only (CR-6348, Aaron 5 Oct 2026, reversing the 20 Sep restriction).
export const SUPPLIER_RESTRICTED_COLUMNS = [
  'buyer_id',
  'ppv',
  'contract_unit_price',
  'contract_lot_price',
  'contract_price',
  'converted_unit_price',
  'converted_total_value',
  'converted_outstanding_value',
  'exchange_rate',
];

// Filter state interface
// CR-3413: Removed dateFrom/dateTo (Delivery From/To), combined status filters
export interface POLinesFilters {
  poNumber: string;
  lineNumber: string;
  supplier: string;
  supplierSearch: string;  // CR-3413: For searchable supplier dropdown
  supplierGroup: string;
  customer: string;
  customerSearch: string;  // CR-3413: For searchable customer/site dropdown
  partNumber: string;
  /** CR-7308: searches the PART MASTER's description, not the line's order text. */
  partDescription: string;
  /** The line's own order text. Kept as its own filter because this grid keeps
   *  the Order Description COLUMN, and a column you cannot filter is half a column. */
  description: string;
  erpOrderType: string;
  status: string;  // CR-3413: Combined status filter (replaces separate status and acknowledgeStatus)
  oddFrom: string;
  oddTo: string;
  on_hold: string;
  // CR-7322: '' | 'customer' | 'supplier' — narrows the On Hold tab to who
  // placed the hold, so the daily digest's split hold rows deep-link exactly.
  on_hold_placed_by: string;
  // CR-7400: the selected buyer codes as a comma-separated list, sent as
  // buyer_id__in (exact match, one or several). Empty = no buyer filter.
  buyerId: string;
  batchSerialNo: string;
  /** CR-7416: the measured verdict — '', 'pending', 'passed' or 'failed'.
   *  Sent as `otif_verdict`: `otif_status` is the ODD Status control's key. */
  otifVerdict: string;
  /** CR-7418: the server's OTIF decision filter (all | not_decided |
   *  overridden | acknowledged). Carried by the daily review digest's deep
   *  link. Lives in `filters` so Clear clears it like anything else. */
  otifDecision: string;
  nextAction: string;
  ppv: string;
  commodityGroup: string;
  commodityType: string;
  // CR-6348: reworked SO Lines filter panel — Company/Supplier hierarchy +
  // legacy status filters.
  companyCountry: string;
  division: string;
  warehouse: string;
  supplierCountry: string;
  supplierCode: string;  // CR-7028: Supplier code filter
  oddStatus: string;
  otdStatus: string;
  criticalOnly: string;  // '' | 'true' — Critical lines only checkbox
  // CR-6347 rework: legacy customer filters the client reported missing.
  supplierType: string;   // '' | 'internal' | 'external'
  strategicOnly: string;  // '' | 'true' — Strategic suppliers only checkbox
}

// CR-3413: Removed dateFrom/dateTo, acknowledgeStatus (now combined into status)
export const DEFAULT_FILTERS: POLinesFilters = {
  poNumber: '',
  lineNumber: '',
  supplier: '',
  supplierSearch: '',
  supplierGroup: '',
  customer: '',
  customerSearch: '',
  partNumber: '',
  partDescription: '',
  description: '',
  erpOrderType: '',
  status: '',
  oddFrom: '',
  oddTo: '',
  on_hold: '',
  on_hold_placed_by: '',
  buyerId: '',
  batchSerialNo: '',
  otifVerdict: '',
  otifDecision: '',
  nextAction: '',
  ppv: '',
  commodityGroup: '',
  commodityType: '',
  // CR-6348
  companyCountry: '',
  division: '',
  warehouse: '',
  supplierCountry: '',
  supplierCode: '',  // CR-7028
  oddStatus: '',
  otdStatus: '',
  criticalOnly: '',
  // CR-6347 rework
  supplierType: '',
  strategicOnly: '',
};

// CR-6347 rework: Supplier Type — internal/external relative to the customer
// organisation in scope (backend: core.internal_suppliers). Same values and
// labels as the Dashboard Classification section.
export const SUPPLIER_TYPE_OPTIONS = [
  { value: 'internal', label: 'Internal Suppliers' },
  { value: 'external', label: 'External Suppliers' },
];

// CR-3413: Unified status options combining Status and Acknowledge Status
// Note: 'changed' maps to change_proposed_supplier + change_proposed_customer in the backend
// Note: 'on_hold' maps to the on_hold boolean flag (not an acknowledge_status)
// Note: 'closed' maps to PO line status 'received'
// Raw ERP order-type codes (DE: 4 = scheduling agreement, 1 = standard/spot).
export const ERP_ORDER_TYPE_OPTIONS = [
  { value: '4', label: 'Scheduling Agreement (4)' },
  { value: '1', label: 'Standard / Spot (1)' },
  { value: '2', label: 'Type 2' },
  { value: '3', label: 'Type 3' },
];

/**
 * CR-6347 rework: the customer panel's "Order Type" dropdown is driven by the
 * ERP codes actually present in scope (filter-options `erp_order_types`), so
 * a tenant whose feed carries no codes sees an honest empty list. Known codes
 * keep their friendly label; anything else is shown as "Type <code>".
 */
export function erpOrderTypeOptions(codes: string[] | undefined): Array<{ value: string; label: string }> {
  return (codes ?? []).map((code) => ({
    value: code,
    label: ERP_ORDER_TYPE_OPTIONS.find((o) => o.value === code)?.label ?? `Type ${code}`,
  }));
}

export const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'changed', label: 'Changed' },
  { value: 'change_declined', label: 'Change Declined' },
  { value: 'wip', label: 'WIP' },
  { value: 'declined', label: 'Declined' },
  { value: 'despatched', label: 'Dispatched' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

// Filter options from API
export interface FilterOptions {
  // CR-6439 / CR-6347 rework: `customers` is the company-site ids the
  // supplier has PO lines with, for the Company Site -> Supplier Site cascade.
  suppliers: Array<{ id: number; name: string; group?: number | null; country?: string | null; customers?: number[] }>;
  customers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  commodity_groups: Array<{ id: number; name: string }>;
  commodity_types: Array<{ id: number; name: string; commodity_group_id: number }>;
  supplier_groups?: Array<{ id: number; name: string }>;
  // CR-6348: option-lists for the reworked SO Lines filter panel.
  divisions?: Array<{ id: number; name: string }>;
  warehouses?: Array<{ id: number; name: string; site: number }>;
  site_countries?: string[];
  supplier_countries?: string[];
  // CR-6347 rework: ERP order-type codes present in scope (Order Type filter).
  erp_order_types?: string[];
  // CR-7400: ERP buyer codes present on the PO lines in scope, sorted. Drives
  // the Buyer ID multi-select, which matches them exactly (buyer_id__in).
  buyer_ids?: string[];
}

// CR-6348: "ODD Status" maps to the PO line's stored otif_status column
// (adherence to the agreed / Original Delivery Date).
export const ODD_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending – Not Yet Due' },
  { value: 'otif', label: 'OTIF – On Time In Full' },
  { value: 'early', label: 'Early Delivery' },
  { value: 'late', label: 'Late Delivery' },
  { value: 'short', label: 'Short Delivery' },
  { value: 'arrear', label: 'Arrear – Overdue & Not Received' },
];

// CR-6348: "OTD Status to LSPD" — on-time delivery measured against the Latest
// Supplier Promise Date (backend annotates last receipt vs supplier_promise_date).
export const OTD_LSPD_STATUS_OPTIONS = [
  { value: 'on_time', label: 'On Time' },
  { value: 'late', label: 'Late' },
  { value: 'not_received', label: 'Not Received' },
];

// Acknowledge status configuration. EXPEDITING-PARITY PR 2: the table itself
// moved to ../../lineStatus, which owns the line's ONE status (a held line's
// status is the hold). Re-exported here because a dozen modules import it from
// this path, and two copies of a mapping is the defect being fixed.
export type { StatusConfig } from '../../lineStatus';
export { ACKNOWLEDGE_STATUS_CONFIG } from '../../lineStatus';

// Next action result type
export interface NextActionResult {
  text: string;
  className: string;
}

// Re-export PurchaseOrderLine for convenience
export type { PurchaseOrderLine };
